import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Autocomplete,
  Chip,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import type { Task, TaskFormData, Priority } from '@/types'
import { ROOMS, TASK_TYPES } from '@/types'
import { useFamilyStore } from '@/store/useFamilyStore'
import { useSupplyStore } from '@/store/useSupplyStore'
import { useTaskStore } from '@/store/useTaskStore'
import RecurrenceBuilder from './RecurrenceBuilder'

interface TaskFormProps {
  open: boolean
  onClose: () => void
  task?: Task | null
}

export default function TaskForm({ open, onClose, task }: TaskFormProps) {
  const members = useFamilyStore((state) => state.members)
  const supplies = useSupplyStore((state) => state.supplies)
  const addTask = useTaskStore((state) => state.addTask)
  const updateTask = useTaskStore((state) => state.updateTask)

  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    room: 'Kitchen',
    task_type: 'Quick Tidy',
    assigned_to: '',
    priority: 'Medium',
    estimated_duration: 30,
    notes: '',
    recurrence_rule: null,
    start_date: new Date().toISOString(),
    end_date: null,
    skip_dates: [],
    supply_ids: [],
  })

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        room: task.room,
        task_type: task.task_type,
        assigned_to: task.assigned_to,
        priority: task.priority,
        estimated_duration: task.estimated_duration,
        notes: task.notes || '',
        recurrence_rule: task.recurrence_rule,
        start_date: task.start_date,
        end_date: task.end_date,
        skip_dates: task.skip_dates,
        supply_ids: [],
      })
    } else if (members.length > 0 && !formData.assigned_to) {
      setFormData((prev) => ({ ...prev, assigned_to: members[0].id }))
    }
  }, [task, members])

  const handleChange = (field: keyof TaskFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (task) {
      await updateTask(task.id, formData)
    } else {
      await addTask(formData)
    }
    onClose()
    resetForm()
  }

  const resetForm = () => {
    setFormData({
      title: '',
      room: 'Kitchen',
      task_type: 'Quick Tidy',
      assigned_to: members[0]?.id || '',
      priority: 'Medium',
      estimated_duration: 30,
      notes: '',
      recurrence_rule: null,
      start_date: new Date().toISOString(),
      end_date: null,
      skip_dates: [],
      supply_ids: [],
    })
  }

  const handleClose = () => {
    onClose()
    if (!task) resetForm()
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            fontWeight: 700,
          }}
        >
          {task ? 'Edit' : 'Create'} Task
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5} mt={3}>
            {/* Title */}
            <TextField
              label="Task Title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              fullWidth
              placeholder="e.g., Clean Kitchen Counters"
            />

            {/* Room */}
            <FormControl fullWidth>
              <InputLabel>Room</InputLabel>
              <Select
                value={formData.room}
                label="Room"
                onChange={(e) => handleChange('room', e.target.value)}
              >
                {ROOMS.map((room) => (
                  <MenuItem key={room} value={room}>
                    {room}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Task Type */}
            <FormControl fullWidth>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={formData.task_type}
                label="Task Type"
                onChange={(e) => handleChange('task_type', e.target.value)}
              >
                {TASK_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Assigned To */}
            <FormControl fullWidth>
              <InputLabel>Assign To</InputLabel>
              <Select
                value={formData.assigned_to}
                label="Assign To"
                onChange={(e) => handleChange('assigned_to', e.target.value)}
              >
                {members.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Priority */}
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) =>
                  handleChange('priority', e.target.value as Priority)
                }
              >
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </Select>
            </FormControl>

            {/* Estimated Duration */}
            <TextField
              label="Estimated Duration (minutes)"
              type="number"
              value={formData.estimated_duration}
              onChange={(e) =>
                handleChange('estimated_duration', parseInt(e.target.value) || 0)
              }
              inputProps={{ min: 5, step: 5 }}
              fullWidth
            />

            {/* Supplies */}
            <Autocomplete
              multiple
              options={supplies}
              getOptionLabel={(option) => option.name}
              value={supplies.filter((s) => formData.supply_ids.includes(s.id))}
              onChange={(_, newValue) => {
                handleChange(
                  'supply_ids',
                  newValue.map((s) => s.id)
                )
              }}
              renderInput={(params) => (
                <TextField {...params} label="Supplies Needed" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />

            {/* Start Date */}
            <DateTimePicker
              label="Start Date & Time"
              value={new Date(formData.start_date)}
              onChange={(newValue) =>
                handleChange('start_date', newValue?.toISOString() || new Date().toISOString())
              }
              slotProps={{ textField: { fullWidth: true } }}
            />

            {/* Recurrence Builder */}
            <RecurrenceBuilder
              value={formData.recurrence_rule}
              onChange={(rrule) => handleChange('recurrence_rule', rrule)}
            />

            {/* End Date (for recurring tasks) */}
            {formData.recurrence_rule && (
              <DateTimePicker
                label="End Date (Optional)"
                value={formData.end_date ? new Date(formData.end_date) : null}
                onChange={(newValue) =>
                  handleChange('end_date', newValue?.toISOString() || null)
                }
                slotProps={{ textField: { fullWidth: true } }}
              />
            )}

            {/* Notes */}
            <TextField
              label="Notes / Instructions"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Any special instructions or details..."
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} size="large">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.title || !formData.assigned_to}
            size="large"
            sx={{
              minWidth: 120,
            }}
          >
            {task ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}
